import React from "react";
import type { SVGProps } from "react";

export type CentralIconBaseProps = {
  size?: string | number;
  ariaHidden?: boolean;
  mode?: "masked" | "raw";
  maskId?: string;
} & SVGProps<SVGSVGElement>;

type Props = CentralIconBaseProps & { ariaLabel?: string };

const ModernCentralIconBase: React.FC<Props> = (props) => {
  const instanceId = React.useId();
  return <CentralIconSvg {...props} instanceId={instanceId} />;
};

let nextLegacyId = 0;

// React's Server Components runtime exposes useId but not Component. Only
// define the legacy class when it is needed, so modern imports stay RSC-safe.
const LegacyCentralIconBase =
  typeof React.useId === "function"
    ? undefined
    : class extends React.Component<
        { iconProps: Props },
        { instanceId?: string }
      > {
        state: { instanceId?: string } = {};

        componentDidMount() {
          // Defer legacy IDs until mount so server and initial client markup agree.
          this.setState({ instanceId: `legacy-${++nextLegacyId}` });
        }

        render() {
          return (
            <CentralIconSvg
              {...this.props.iconProps}
              instanceId={this.state.instanceId}
            />
          );
        }
      };

export const CentralIconBase: React.FC<Props> = (props) =>
  LegacyCentralIconBase ? (
    <LegacyCentralIconBase iconProps={props} />
  ) : (
    <ModernCentralIconBase {...props} />
  );

const CentralIconSvg: React.FC<Props & { instanceId?: string }> = ({
  children,
  size = 24,
  ariaLabel,
  color,
  ariaHidden = true,
  style,
  mode = "masked",
  maskId,
  instanceId,
  ...props
}) => {
  const uniqueMaskId = instanceId ? `${maskId}-${instanceId}` : maskId;
  const masked = mode !== "raw" && !!maskId;
  return (
    <svg
      {...props}
      aria-hidden={ariaHidden}
      role={ariaHidden ? undefined : "img"}
      width={typeof size === "number" ? `${size}px` : size}
      height={typeof size === "number" ? `${size}px` : size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ color, ...style }}
    >
      {ariaLabel && !ariaHidden && <title>{ariaLabel}</title>}
      {masked ? (
        <>
          <mask
            id={uniqueMaskId}
            maskUnits="userSpaceOnUse"
            x="0"
            y="0"
            width="24"
            height="24"
          >
            <rect width="24" height="24" fill="#000" />
            <g fill="none" style={{ color: "#fff" }}>
              {children}
            </g>
          </mask>
          <rect
            width="24"
            height="24"
            fill="currentColor"
            mask={`url(#${uniqueMaskId})`}
          />
        </>
      ) : (
        children
      )}
    </svg>
  );
};
